/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-13 00:00:00
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import { Button, TextContainer, TextStyle } from '@shopify/polaris';
import { MobileCancelMajor } from '@shopify/polaris-icons';

import CustomModal from './custom-modal';

import { getShop, isStoreAvailablePersonalizationCampaign } from 'src/helpers';
import { updateTenantApp, updateLocalTenantApp } from 'actions/oauth-actions';
import arrowRight from 'src/assets/images/campaigns/arrow-right-minor.png';

const MarketingCampaignPopup = props => {
  const { i18n, tenantApp, updateTenantAppLoading } = props;
  const [showModal, setShowModal] = useState(false);

  const getKeyDismiss = shop => `${shop}-dismissMarketingCampaignPopup`;

  const checkDismissedModal = () => {
    const shop = getShop();
    const selectedPlan = _.get(tenantApp, 'selectedPlan', '');
    const personalizationCampaignStatus = _.get(tenantApp, 'personalizationCampaignStatus', '');
    const firstInstalledAt = _.get(tenantApp, 'firstInstalledAt');

    // Checking selected plan or install after 18/06
    if (firstInstalledAt > 1718668800 || !isStoreAvailablePersonalizationCampaign(selectedPlan)) {
      return;
    }

    // Check time and status
    const now = moment().startOf();
    const isShowTime =
      now.isAfter(moment('2024-06-18 00:00:00')) && now.isBefore(moment('2024-06-24 23:59:59')); // Date to test

    if (!isShowTime || !_.isEmpty(personalizationCampaignStatus)) {
      return;
    }

    try {
      const dismissed = localStorage.getItem(getKeyDismiss(shop));
      if (dismissed !== 'true') setShowModal(true);
    } catch (error) {}
  };

  const onDismiss = () => {
    const shop = getShop();
    try {
      localStorage.setItem(getKeyDismiss(shop), true);
    } catch (error) {}

    setShowModal(false);
  };

  // Handle change personalization campaign status
  const handleRegisterCampaign = () => {
    const { updateTenantApp, updateLocalTenantApp } = props;
    const meta = {
      onSuccess: () => {
        setShowModal(false);
        updateLocalTenantApp({ showConfirmedPersonalizationCampaignPopup: true });
        onDismiss();
      }
    };
    updateTenantApp({ personalizationCampaignStatus: 'registered' }, meta);
  };

  useEffect(() => {
    checkDismissedModal();
    // eslint-disable-next-line
  }, []);

  return (
    <CustomModal
      open={showModal}
      className="BC-MarketingCampaignPopup-Container"
      clickBackdrop={onDismiss}>
      <div className="BC-MarketingCampaignPopup-Wrapper">
        <div className="BC-MarketingCampaignPopup">
          <div className="popup-detail">
            <div className="popup-content">
              <div className="title">
                {i18n.translate('AdminHTML.MarketingCampaign.RequestPopup.title', {
                  t1: (
                    <span className="text-gradient">
                      {i18n.translate('AdminHTML.MarketingCampaign.RequestPopup.title_t1')}
                    </span>
                  )
                })}
              </div>
              <TextContainer>
                <p>
                  {i18n.translate('AdminHTML.MarketingCampaign.RequestPopup.content', {
                    t1: (
                      <TextStyle variation="strong">
                        {i18n.translate('AdminHTML.MarketingCampaign.RequestPopup.content_t1')}
                      </TextStyle>
                    ),
                    t2: (
                      <TextStyle variation="strong">
                        {i18n.translate('AdminHTML.MarketingCampaign.RequestPopup.content_t2')}
                      </TextStyle>
                    ),
                    t3: (
                      <TextStyle variation="strong">
                        {i18n.translate('AdminHTML.MarketingCampaign.RequestPopup.content_t3')}
                      </TextStyle>
                    )
                  })}
                </p>
                <div className="BC-Iframe-Container__Wrapper">
                  <div className="BC-Iframe-Container__Center">
                    <div className="BC-Iframe-Container">
                      <iframe
                        title="Personalization Marketing Campaign"
                        src="https://www.youtube.com/embed/7IjSUYxJ9D4?si=0cVADHXWeut5tktq"
                        frameBorder="0"
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                </div>
              </TextContainer>
            </div>
            <Button
              destructive
              size="slim"
              onClick={handleRegisterCampaign}
              loading={updateTenantAppLoading}>
              {i18n.translate(`AdminHTML.MarketingCampaign.RequestPopup.action`)}
              <img src={arrowRight} alt="action-icon-primary" />
            </Button>
          </div>

          <div className="closeBtn">
            <Button onClick={onDismiss} icon={MobileCancelMajor} plain />
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  return {
    tenantApp,
    updateTenantAppLoading: _.get(state.oauth, 'loadingUpdate', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateTenantApp: (body, meta) => dispatch(updateTenantApp(body, meta, ownProps.i18n)),
  updateLocalTenantApp: body => dispatch(updateLocalTenantApp(body))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(MarketingCampaignPopup));
